import React, { useState, useEffect, useMemo } from "react";
import { Grid, Typography, Box, Slider, styled } from "@mui/material";


import ButtonComponent from "../../components/saving-league/buttonBottom";

const achivepointsimg = "https://d2sriifpjiqv0p.cloudfront.net/savings-league/archive.png";
const thumbimg = "https://d2sriifpjiqv0p.cloudfront.net/savings-league/thumbimg.png";
const points30bg = "https://d2sriifpjiqv0p.cloudfront.net/savings-league/points30bg.png";


const VerticalSlider = styled(Slider)({
  height: 400,
});

export default function AchievePoints() {
  const [userData, setUserData] = useState(null);
  
  // Helpers to safely read numeric values from localStorage
  const getNum = (key, d = 0) => {
    const v = localStorage.getItem(key);
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
  };

  // Fetch user data from API
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const ck_id = params.get('ck_id') || localStorage.getItem('savings_league_ck_id_sep');
        
        if (ck_id) {
          const response = await fetch(`https://leaderboard-1.earningshub.co/api/user/getleaderboard_cdl?ck_id=${ck_id}`);
          const data = await response.json();
          
          if (data?.user_data?.user) {
            setUserData(data.user_data.user);
            console.log('🔍 AchievePoints API Response:', data.user_data.user);
          }
        }
      } catch (error) {
        console.error('Error fetching user data in achievePoints:', error);
      }
    };

    fetchUserData();
  }, []);

  // Function to get formatted date from API data only
  const getFormattedDate = () => {
    // Only show date when userData is loaded
    if (!userData) {
      return 'Loading...';
    }
    
    // Try multiple possible locations for the date from API only
    const dateValue = userData?.maxUpdatedAt || 
                     userData?.leaderboards_saving_league_sept?.updatedAt ||
                     userData?.updatedAt;
    
    console.log('🔍 AchievePoints Date Debug:', {
      maxUpdatedAt: userData?.maxUpdatedAt,
      leaderboardUpdatedAt: userData?.leaderboards_saving_league_sept?.updatedAt,
      userUpdatedAt: userData?.updatedAt,
      finalDateValue: dateValue
    });
    
    if (dateValue) {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        date.setDate(date.getDate() - 1);
        return date.toLocaleDateString('en-GB', { 
          timeZone: 'Asia/Kolkata'
        });
      }
    }
    return 'N/A';
  };

  // Bonus labels aligned to milestones [0, m1..m7]
  const marks3 = useMemo(
    () => ["0", "+ 50 Points", "+ 100 Points", "+ 150 Points", "+ 200 Points", "+ 250 Points", "+ 400 Points", "+ 1000 Points"],
    []
  );

  // Current points (controls where the thumb sits)
  const [currentPoints, setCurrentPoints] = useState(0);

  // Milestones as objects: [{value,label}, ...] for [0, m1..m7]
  const [marks2, setMarks2] = useState([]);

  // Compute milestones once on mount (or when LS changes if you add an event)
  useEffect(() => {
    const ms = [0, ...Array.from({ length: 7 }, (_, i) => getNum(`milestone_${i + 1}`, 0))];
    setMarks2(ms.map((v) => ({ value: v, label: `${v} Points` })));
    setCurrentPoints(getNum("savings_points", 0));
  }, []);

  // Guard: if milestones not ready yet, avoid render glitches
  const maxMilestone = useMemo(() => {
    if (!marks2.length) return 0;
    return Number(marks2[marks2.length - 1]?.value) || 0;
  }, [marks2]);

  // Slider is inverted visually (0 at bottom, max at top), so we use (max - current)
  const invertedSliderValue = Math.max(0, maxMilestone - currentPoints);

  // No-op: component is controlled, but MUI still passes onChange
  const handleSliderChange = (event, newValue) => {
    // If you ever want interactive sliding, map it back as:
    // const nextPoints = Math.max(0, maxMilestone - Number(newValue));
    // setCurrentPoints(nextPoints);
  };

  return (
    <>
    <Grid container sx={{ background: "#fff" }}>
      <Grid item xs={12} sm={12} md={4} lg={4}></Grid>

      <Grid item xs={12} sm={12} md={4} lg={4} sx={{ background: "#1F162E" }}>
        <Grid
          sx={{
            background: "#1F162E",
            width: "100%",
            paddingBottom: "30%",
            height:'100%',
          }}
        >
          <Grid pb={2}>
            <img
              src="https://d2sriifpjiqv0p.cloudfront.net/diwali/milestone.png"
              style={{ width: "100%" }}
              alt="Milestone header"
            />
          </Grid>

          <Grid pt={2} px={3}>
            <Typography
              sx={{
                fontWeight: "700",
                fontSize: "1rem",
                fontFamily: "metropolis",
                textAlign: "center",
                lineHeight: "140%",
              }}
            >
              Achieve point milestones
            </Typography>
            <Typography
              sx={{
                fontWeight: "500",
                fontSize: "12px",
                fontFamily: "metropolis",
                textAlign: "center",
                lineHeight: "140%",
              }}
              mt={1}
            >
              Unlock extra points at every milestone
            </Typography>
          </Grid>

          <Typography
            sx={{
              color: "#fff",
              textAlign: "center",
              fontFamily: "Metropolis",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "10px",
              paddingBottom: "32px",
              paddingTop: "20px",
            }}
          >
            Current Points {currentPoints}
          </Typography>

          <Grid item sx={{ height: "368px", boxSizing: "border-box", marginBottom: "32px" }}>
            <Box
              sx={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                height: "368px",
                boxSizing: "border-box",
                left: "-20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  height: "368px",
                  zIndex: "11",
                  boxSizing: "border-box",
                  position: "relative",
                }}
              >
                {/* Background slider (labels of raw milestone values) */}
                <VerticalSlider
                  aria-label="Milestone background"
                  defaultValue={invertedSliderValue}
                  value={invertedSliderValue}
                  onChange={handleSliderChange}
                  valueLabelDisplay="off"
                  marks={marks2.map((mark) => ({
                    value: Math.max(0, maxMilestone - Number(mark.value)),
                    label: mark.label,
                  }))}
                  orientation="vertical"
                  min={0}
                  max={maxMilestone}
                  sx={{
                    color: "transparent",
                    boxShadow: "0px 0px 0px",
                    borderRadius: "20px",
                    zIndex: "222",
                    position: "relative",
                    height: "368px",
                    left: "-50px",
                    boxSizing: "border-box",
                    "& .MuiSlider-thumb": { display: "none" },
                    "& .MuiSlider-track": { width: 6 },
                    "& .MuiSlider-rail": { display: "none" },
                    "& .MuiSlider-mark": { display: "none" },
                    "& .MuiSlider-markLabel": {
                      color: "#FFFFFF",
                      textAlign: "center",
                      fontFamily: "Metropolis",
                      fontSize: "10px",
                      fontStyle: "normal",
                      lineHeight: "10px",
                      fontWeight: 400,
                      marginLeft: "10px",
                    },
                  }}
                />

                {/* Foreground slider (bonus labels + active coloring) */}
                <VerticalSlider
                  aria-label="Milestone bonuses"
                  className="achivePointSlider"
                  defaultValue={invertedSliderValue}
                  value={invertedSliderValue}
                  onChange={handleSliderChange}
                  valueLabelDisplay="off"
                  marks={marks2.map((mark, ind) => {
                    const milestoneVal = Number(mark.value);
                    const isActive = currentPoints >= milestoneVal; // inclusive
                    const bonusLabel = marks3[ind] === "0" ? "" : marks3[ind];
                    return {
                      value: Math.max(0, maxMilestone - milestoneVal),
                      label: (
                        <span
                          style={{
                            color: isActive ? "#FE8B37" :  "#4A3B8A",
                            fontWeight: 600,
                          }}
                        >
                          {bonusLabel}
                        </span>
                      ),
                    };
                  })}
                  orientation="vertical"
                  min={0}
                  max={maxMilestone}
                  sx={{
                    color: "transparent",
                    boxShadow: "0px 0px 0px",
                    borderRadius: "20px",
                    zIndex: "222",
                    position: "relative",
                    height: "368px",
                    boxSizing: "border-box",
                    "& .MuiSlider-thumb": {
                      height: 26,
                      width: 37,
                      boxShadow: "inherit !important",
                      background: `url(${thumbimg})`,
                      border: "0px solid currentColor",
                      "&:focus, &:hover, &.Mui-active": { boxShadow: "inherit" },
                      ":before": { boxShadow: "0px 0px 0px" },
                    },
                    "& .MuiSlider-track": {
                      width: 6,
                      backgroundColor: "#4A3B8A",
                    },
                    "& .MuiSlider-rail": {
                      backgroundColor: "#FF8C00",
                      opacity: 1,
                      width: 6,
                    },
                    "& .MuiSlider-mark": {
                      backgroundColor: "#FF8C00",
                      height: 2,
                      width: 14,
                      borderRadius: "20px",
                    },
                    "& .MuiSlider-mark.MuiSlider-markActive": {
                      backgroundColor: "#4a3b8a",
                      height: 2,
                      width: 14,
                      borderRadius: "20px",
                    },
                    "& .MuiSlider-markLabel": {
                      textAlign: "center",
                      fontFamily: "Metropolis",
                      fontSize: "12px",
                      fontStyle: "normal",
                      lineHeight: "10px",
                      paddingLeft: "10px",
                    },
                    "& .MuiSlider-markLabel.MuiSlider-markLabelActive": {
                      color: "#9B59B6",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      width: "106px",
                      height: "52px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      backgroundRepeat: "no-repeat",
                    },
                  }}
                />
              </div>
            </Box>
          </Grid>

          <Grid>
            <Typography
              sx={{
                color: "#7524CE",
                textAlign: "center",
                fontFamily: "Metropolis",
                fontSize: "1rem",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "10px",
                paddingTop: "20px",
              }}
            >
              You are at <span style={{ color: "#FE8B37" }}>{currentPoints}</span>
            </Typography>
            <Box>
              <Typography
                sx={{
                  color: "rgba(255, 255, 255, 0.50)",
                  textAlign: "center",
                  fontFamily: "Metropolis",
                  fontSize: "10px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  paddingBottom: "6px",
                  lineHeight: "140%",
                }}
                pt={2}
              >
                <span>*Last updated on {getFormattedDate()}</span>

              </Typography>
            </Box>
          </Grid>
        </Grid>

        <a href="cashkaro://home">
          <ButtonComponent title="Start Collecting Points" />
        </a>
      </Grid>

      <Grid item xs={12} sm={12} md={4} lg={4}></Grid>
    </Grid>
    <style>
            {
                `
                body{background: #1f162e;}
                
                `
            }
        </style>
    </>
    
  );
}
